import { uniqueBy } from 'remeda'

import { AdaptedProduct } from '@/types'

export type SimpleOption = { label: string; value: string }

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const normalizeShapeEntries = (
  shapeID: SimpleOption[] | null | undefined,
): SimpleOption[] => {
  return shapeID ?? []
}

export const aggregateProductShapeIDs = (
  product: AdaptedProduct,
): SimpleOption[] => {
  // Only include shapes from batched occurrences that have a defined quantity
  const validBatchedProducts = (product?.batchedProducts ?? []).filter((p) => {
    const qtyValue = p?.['quantity.number']?.value
    return qtyValue != null
  })

  const entries = [product, ...validBatchedProducts]
    .flatMap((p) => normalizeShapeEntries(p?.shapeID))
    .filter((entry): entry is SimpleOption => !!entry?.value)

  return uniqueBy(entries, (e) => e.value)
}

export const normalizeShapeValueForControl = (
  product: AdaptedProduct,
): SimpleOption[] => {
  const isBatched = (product?.batchedProducts?.length ?? 0) > 0
  if (isBatched) return aggregateProductShapeIDs(product)

  const current = product?.shapeID
  if (!current || current.length === 0) return []
  return current
}

export const formatShapeLabelsCompact = (
  shapeIDs: SimpleOption[] | SimpleOption | null | undefined,
  options?: {
    threshold?: number
    prefixLength?: number
  },
): string => {
  const threshold = options?.threshold ?? 3
  const prefixLength = options?.prefixLength

  if (!shapeIDs) return ''

  const list: SimpleOption[] = Array.isArray(shapeIDs) ? shapeIDs : [shapeIDs]
  // Drop entries with empty/whitespace-only labels to avoid leading/trailing commas
  const filtered: SimpleOption[] = list.filter(
    (s) =>
      !!(
        s?.label &&
        s.label.trim().length > 0 &&
        !UUID_PATTERN.test(s.label.trim())
      ),
  )

  if (filtered.length === 0) return ''

  const takeFirstLettersUpper = (input: string, count: number): string => {
    const result: string[] = []
    for (const ch of input.trim()) {
      if (/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(ch)) {
        result.push(ch.toUpperCase())
        if (result.length >= count) break
      }
    }
    return result.join('')
  }

  const alphaNumCompare = (a: string, b: string): number =>
    (a || '').localeCompare(b || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })

  const isCodePrefix = (value: string): boolean =>
    /^[A-Z0-9À-ÖØ-Þ/]+$/.test(value)

  const resolveCompactLabel = (label: string): string => {
    const trimmed = label.trim()
    if (prefixLength !== undefined) {
      return takeFirstLettersUpper(trimmed, prefixLength) || trimmed
    }

    const codePrefixMatch = trimmed.match(/^([A-Za-zÀ-ÖØ-öø-ÿ0-9/]+)\s*[-_]/)
    const codePrefix = codePrefixMatch?.[1]?.trim()
    if (codePrefix && isCodePrefix(codePrefix)) {
      return codePrefix.toUpperCase()
    }

    const signedLevelMatch = trimmed.match(
      /^([A-Za-zÀ-ÖØ-öø-ÿ0-9/]+)[+-]\d+(?:\.\d+)?$/,
    )
    if (signedLevelMatch?.[1]) {
      return signedLevelMatch[1].toUpperCase()
    }

    return trimmed
  }

  const sorted = [...filtered].sort((a, b) =>
    alphaNumCompare(a?.label, b?.label),
  )

  // In compact mode, code-like section labels keep only their building prefix,
  // then duplicate prefixes are collapsed.
  if (filtered.length > threshold) {
    const result: string[] = []
    const usedLabels = new Set<string>()
    for (const shape of sorted) {
      const compactLabel = resolveCompactLabel(shape?.label ?? '')

      if (!usedLabels.has(compactLabel)) {
        result.push(compactLabel)
        usedLabels.add(compactLabel)
      }
    }

    return result.join(', ')
  }

  return sorted.map((s) => s.label).join(', ')
}
